import React, { useEffect, useMemo, useState } from 'react';
import { Search, Eye, Clock, Film, Play, Scissors, Archive, ExternalLink } from 'lucide-react';
import { useTwitchVods, useTwitchClips } from '../hooks/useTwitch';
import { streamer } from '../mock';

const SKELETON_KEYS = ['sk1', 'sk2', 'sk3', 'sk4', 'sk5', 'sk6', 'sk7', 'sk8'];

const CLIP_SORTS = [
  { key: 'views', label: 'Best bekeken', Icon: Eye },
  { key: 'new', label: 'Nieuwste', Icon: Clock }
];

function ClipSortToggle({ value, onChange }) {
  return (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/[0.04]"
      data-testid="clip-sort-toggle"
    >
      {CLIP_SORTS.map(({ key, label, Icon }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            data-testid={`clip-sort-${key}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              active
                ? 'bg-pink-600 text-white shadow-sm'
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function VodThumb({ v }) {
  const [broken, setBroken] = useState(false);
  const [retry, setRetry] = useState(0);
  const onError = () => {
    if (retry === 0) setTimeout(() => setRetry(1), 1500);
    else setBroken(true);
  };
  if (!v.thumb || broken) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-violet-950 via-[#1a0b2e] to-zinc-950" data-testid={`vod-thumb-fallback-${v.id}`}>
        <img src="/jordievoorje-logo.png" alt="" className="w-1/2 opacity-70" draggable={false} />
      </div>
    );
  }
  return (
    <img
      key={retry}
      src={retry ? `${v.thumb}${v.thumb.includes('?') ? '&' : '?'}r=${retry}` : v.thumb}
      alt={v.title}
      onError={onError}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
    />
  );
}

function VodCard({ v, onPlay, accent }) {
  const archived = Boolean(v.archived);
  const Tag = archived ? 'a' : 'button';
  const linkProps = archived
    ? { href: streamer.socials.twitch, target: '_blank', rel: 'noreferrer' }
    : { onClick: () => onPlay(v), type: 'button' };
  return (
    <Tag
      {...linkProps}
      className={`group block text-left rounded-xl overflow-hidden bg-white/[0.03] border border-white/10 ${accent.hoverBorder} transition-all hover:-translate-y-1`}
      data-testid={`vod-card-${v.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-zinc-900">
        <VodThumb v={v} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className={`w-14 h-14 rounded-full ${archived ? 'bg-zinc-700/90' : accent.playBg} flex items-center justify-center`}>
            {archived ? <Archive className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white fill-current ml-0.5" />}
          </span>
        </div>
        {v.duration && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded bg-black/70 backdrop-blur text-xs text-white font-mono">
            <Clock className="w-3 h-3" /> {v.duration}
          </div>
        )}
        {v.game && (
          <div className={`absolute top-2 left-2 px-2 py-1 rounded ${archived ? 'bg-zinc-700/90' : accent.tagBg} text-[10px] font-bold text-white uppercase tracking-wider max-w-[60%] truncate`} data-testid={archived ? `vod-archived-badge-${v.id}` : undefined}>
            {v.game}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className={`text-white font-semibold leading-snug line-clamp-2 ${accent.titleHover} transition-colors`}>
          {v.title}
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" /> {Number(v.views).toLocaleString('nl-NL')}
          </span>
          <span>•</span>
          <span>{v.date}</span>
          {archived && (
            <>
              <span>•</span>
              <span className="text-zinc-400">niet meer terug te kijken</span>
            </>
          )}
        </div>
      </div>
    </Tag>
  );
}

function VodSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {SKELETON_KEYS.map((key) => (
        <div
          key={key}
          className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden animate-pulse"
        >
          <div className="aspect-video bg-white/5" />
          <div className="p-4 space-y-2">
            <div className="h-4 bg-white/10 rounded w-3/4" />
            <div className="h-3 bg-white/5 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function VodEmpty({ message }) {
  return (
    <div className="text-center py-20 text-zinc-500">{message}</div>
  );
}

function VodNoStreams() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-12 text-center" data-testid="vod-no-streams">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
        <Film className="w-7 h-7 text-violet-300" />
      </div>
      <h3 className="mt-5 text-xl md:text-2xl font-black text-white">Nog geen streams om terug te kijken</h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
        Twitch bewaart VODs maar een paar weken. Zodra er een nieuwe stream is geweest verschijnt hij hier automatisch — ook als Twitch hem later weer verwijdert.
      </p>
      <a
        href={streamer.socials.twitch}
        target="_blank"
        rel="noreferrer"
        data-testid="vod-no-streams-twitch-link"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-colors"
      >
        Volg op Twitch <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}

function VodResults({ items, onPlay, accent }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {items.map((v) => (
        <VodCard key={v.id} v={v} onPlay={onPlay} accent={accent} />
      ))}
    </div>
  );
}

const VOD_ACCENT = {
  hoverBorder: 'hover:border-violet-500/40',
  playBg: 'bg-violet-600/90',
  tagBg: 'bg-violet-600/90',
  titleHover: 'group-hover:text-violet-300'
};

const CLIP_ACCENT = {
  hoverBorder: 'hover:border-pink-500/40',
  playBg: 'bg-pink-600/90',
  tagBg: 'bg-pink-600/90',
  titleHover: 'group-hover:text-pink-300'
};

const TABS = [
  { key: 'vods', label: 'Twitch VODs', Icon: Film },
  { key: 'clips', label: 'Clips', Icon: Scissors }
];

export default function VodGrid({ onPlay }) {
  const [tab, setTab] = useState('vods');
  const [query, setQuery] = useState('');
  const [clipSort, setClipSort] = useState('views');

  const { vods, loading: vodsLoading } = useTwitchVods();
  const { clips, loading: clipsLoading } = useTwitchClips();

  // No VODs at all (live or archived) → open the Clips tab by default.
  useEffect(() => {
    if (!vodsLoading && !clipsLoading && vods.length === 0 && clips.length > 0) {
      setTab('clips');
    }
  }, [vodsLoading, clipsLoading, vods.length, clips.length]);

  const isClips = tab === 'clips';
  const loading = isClips ? clipsLoading : vodsLoading;
  const source = isClips ? clips : vods;
  const accent = isClips ? CLIP_ACCENT : VOD_ACCENT;

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const matched = source.filter((v) => (v.title || '').toLowerCase().includes(q));
    if (!isClips) return matched;
    const sorted = [...matched];
    if (clipSort === 'new') {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    } else {
      sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    return sorted;
  }, [source, query, isClips, clipSort]);

  let content;
  if (loading) {
    content = <VodSkeleton />;
  } else if (!isClips && vods.length === 0) {
    content = <VodNoStreams />;
  } else if (filtered.length === 0) {
    const emptyMsg = isClips
      ? 'Nog geen clips gevonden.'
      : 'Geen streams gevonden voor deze zoekopdracht.';
    content = <VodEmpty message={emptyMsg} />;
  } else {
    content = <VodResults items={filtered} onPlay={onPlay} accent={accent} />;
  }

  return (
    <section id="vods" className="relative py-24 px-6 lg:px-10 bg-zinc-950/80 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-violet-300 text-sm font-semibold mb-2">
              <Film className="w-4 h-4" /> RECENTE STREAMS
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              Gemist? Bekijk hier terug.
            </h2>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder={isClips ? 'Zoek een clip...' : 'Zoek een stream...'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500/60 focus:bg-white/[0.07] transition-colors"
              data-testid="vod-search-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div
            className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10"
            data-testid="vod-tab-bar"
          >
            {TABS.map(({ key, label, Icon }) => {
              const active = tab === key;
              const activeCls = key === 'clips'
                ? 'bg-pink-600 text-white shadow-lg shadow-pink-900/30'
                : 'bg-violet-600 text-white shadow-lg shadow-violet-900/30';
              return (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`vod-tab-${key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    active
                      ? activeCls
                      : 'text-zinc-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              );
            })}
          </div>

          {isClips && (
            <ClipSortToggle value={clipSort} onChange={setClipSort} />
          )}
        </div>

        {content}
      </div>
    </section>
  );
}
